/** Who works in the shop, and what they may do. */
const staff = {
  en: {
    permissions: {
      edit: "Permissions",
      title: "What {name} may do",
      loadFailed: "Could not load the list of permissions.",
      saved: "{name}'s permissions were saved.",
      everything: "Everything",
      count: { one: "{count} permission", other: "{count} permissions" },
      role: "Role",
      roleName: { owner: "Owner", staff: "Staff" },
      roleHint: {
        owner: "Everything, including staff, devices and the journal.",
        staff: "Only what you tick below.",
      },
      startFrom: "Start from",
      custom: "Custom",
      presets: {
        cashier: "Cashier",
        stock_keeper: "Stock keeper",
        manager: "Manager",
        full: "Full access",
      },
      screens: "Screens",
      bringsCosts: "Made of buy prices — also gives \"See buy prices\"",
      actions: { view: "View", stock: "Adjust stock", edit: "Edit", delete: "Delete" },
      areas: {
        products: "Products & ranges",
        packs: "Packs",
        offers: "Offers",
        purchases: "Sales history",
        preorders: "Pre-orders",
        credit: "Customer credit",
        deliveries: "Deliveries",
        suppliers: "Suppliers & what you owe",
        expenses: "Expenses",
        analytics: "Dashboard & analytics",
        revenue: "Net revenue",
        mixing: "Mixing",
        shop: "Your shop",
        settings: "Settings",
      },
      powersTitle: "Also allowed to",
      powers: {
        see_costs: {
          label: "See buy prices",
          hint: "What you pay suppliers, margins and stock value at cost — on every screen.",
        },
        refund: { label: "Refund sales", hint: "At the till and from Sales history." },
        extend_credit: {
          label: "Sell on credit",
          hint: "Let a customer take goods and pay later.",
        },
        manage_cash: {
          label: "Take money out of the drawer",
          hint: "Withdrawals and paying suppliers from the till.",
        },
      },
      ownerOnlyNote:
        "Staff, devices, the journal and your savings are always yours alone, whatever is ticked.",
    },
    title: "Staff",
    subtitle: "Who works here, and what each person may see and do.",
    add: "Add someone",
    loadFailed: "Could not load your staff list.",
    added: "Added to the shop.",
    addedKeptPassword:
      "Added to the shop. This email already had its own account, so it keeps its existing password — the one you typed was not applied.",
    addFailed: "Could not add that person.",
    phoneRequired: "A new person needs a phone number.",
    phoneTaken: "That phone number already belongs to another account. Leave it empty, or use a different one.",
    changeFailed: "Could not save that change.",
    pinSet: "{name} can now unlock the till.",
    pinFailed: "Could not set that PIN.",
    removed: "{name} no longer works here.",
    removeFailed: "Could not remove that person.",
    existingHint:
      "If this person already has an account, their email is enough — it gets linked rather than duplicated.",
    email: "Email",
    name: "Name",
    phone: "Phone",
    password: "Password",
    newOnly: "(new people only)",
    pin: "Till PIN",
    fourDigits: "(4 digits)",
    adding: "Adding…",
    addToShop: "Add to the shop",
    roles: { owner: "owner", staff: "staff" },
    hasPin: "till PIN set",
    noPin: "no till PIN",
    changePin: "Change PIN",
    setPin: "Set PIN",
    pinPlaceholder: "4 digits",
    savePin: "Save PIN",
    removePin: "Remove PIN",
    you: "you",
    editAccount: "Edit my account",
    myAccount: "My account",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    passwordMismatch: "The new password and its confirmation don't match.",
    savePassword: "Change password",
    passwordUpdated: "Your password was changed.",
    passwordChangeFailed: "Could not change your password.",
    saveAccount: "Save",
    accountUpdated: "Your account was updated.",
    accountUpdateFailed: "Could not save your account.",
    resetPassword: "Reset password",
    resetPasswordConfirm:
      "Reset {name}'s password to the shop's default? They will need to sign in with it and choose a new one.",
    resetPasswordFailed: "Could not reset that password.",
    passwordResetTo:
      "{name}'s password was reset to \"{password}\" — tell them to sign in with it.",
  },

  fr: {
    permissions: {
      edit: "Permissions",
      title: "Ce que {name} peut faire",
      loadFailed: "Impossible de charger la liste des permissions.",
      saved: "Les permissions de {name} ont été enregistrées.",
      everything: "Tout",
      count: { one: "{count} permission", other: "{count} permissions" },
      role: "Rôle",
      roleName: { owner: "Propriétaire", staff: "Employé" },
      roleHint: {
        owner: "Tout, y compris l'équipe, les appareils et le journal.",
        staff: "Seulement ce que vous cochez ci-dessous.",
      },
      startFrom: "Partir de",
      custom: "Personnalisé",
      presets: {
        cashier: "Caissier",
        stock_keeper: "Magasinier",
        manager: "Gérant",
        full: "Accès complet",
      },
      screens: "Écrans",
      bringsCosts: "Fait de prix d'achat — donne aussi « Voir les prix d'achat »",
      actions: { view: "Voir", stock: "Ajuster le stock", edit: "Modifier", delete: "Supprimer" },
      areas: {
        products: "Produits et gammes",
        packs: "Packs",
        offers: "Offres",
        purchases: "Historique des ventes",
        preorders: "Précommandes",
        credit: "Crédit clients",
        deliveries: "Livraisons",
        suppliers: "Fournisseurs et dettes",
        expenses: "Dépenses",
        analytics: "Tableau de bord et analyses",
        revenue: "Revenu net",
        mixing: "Mélanges",
        shop: "Votre boutique",
        settings: "Réglages",
      },
      powersTitle: "Peut aussi",
      powers: {
        see_costs: {
          label: "Voir les prix d'achat",
          hint: "Ce que vous payez aux fournisseurs, les marges et la valeur du stock au coût — sur tous les écrans.",
        },
        refund: { label: "Rembourser des ventes", hint: "À la caisse et depuis l'historique des ventes." },
        extend_credit: {
          label: "Vendre à crédit",
          hint: "Laisser un client emporter la marchandise et payer plus tard.",
        },
        manage_cash: {
          label: "Sortir de l'argent du tiroir",
          hint: "Retraits et paiements aux fournisseurs depuis la caisse.",
        },
      },
      ownerOnlyNote:
        "L'équipe, les appareils, le journal et votre épargne restent toujours à vous seul, quoi qu'il soit coché.",
    },
    title: "Équipe",
    subtitle:
      "Qui travaille ici, et ce que chacun peut voir et faire.",
    add: "Ajouter une personne",
    loadFailed: "Impossible de charger votre équipe.",
    added: "Ajouté à la boutique.",
    addedKeptPassword:
      "Ajouté à la boutique. Cet e-mail avait déjà son propre compte : il garde son mot de passe actuel, celui que vous avez saisi n'a pas été appliqué.",
    addFailed: "Impossible d'ajouter cette personne.",
    phoneRequired: "Une nouvelle personne doit avoir un numéro de téléphone.",
    phoneTaken: "Ce numéro de téléphone appartient déjà à un autre compte. Laissez-le vide ou utilisez-en un autre.",
    changeFailed: "Impossible d'enregistrer cette modification.",
    pinSet: "{name} peut maintenant déverrouiller la caisse.",
    pinFailed: "Impossible d'enregistrer ce code PIN.",
    removed: "{name} ne travaille plus ici.",
    removeFailed: "Impossible de retirer cette personne.",
    existingHint:
      "Si cette personne a déjà un compte, son e-mail suffit — le compte est associé, pas dupliqué.",
    email: "E-mail",
    name: "Nom",
    phone: "Téléphone",
    password: "Mot de passe",
    newOnly: "(nouvelles personnes uniquement)",
    pin: "Code PIN de caisse",
    fourDigits: "(4 chiffres)",
    adding: "Ajout…",
    addToShop: "Ajouter à la boutique",
    roles: { owner: "propriétaire", staff: "employé" },
    hasPin: "code PIN défini",
    noPin: "pas de code PIN",
    changePin: "Changer le code",
    setPin: "Définir un code",
    pinPlaceholder: "4 chiffres",
    savePin: "Enregistrer le code",
    removePin: "Supprimer le code",
    you: "vous",
    editAccount: "Modifier mon compte",
    myAccount: "Mon compte",
    currentPassword: "Mot de passe actuel",
    newPassword: "Nouveau mot de passe",
    confirmPassword: "Confirmer le nouveau mot de passe",
    passwordMismatch:
      "Le nouveau mot de passe et sa confirmation ne correspondent pas.",
    savePassword: "Changer le mot de passe",
    passwordUpdated: "Votre mot de passe a été modifié.",
    passwordChangeFailed: "Impossible de modifier votre mot de passe.",
    saveAccount: "Enregistrer",
    accountUpdated: "Votre compte a été mis à jour.",
    accountUpdateFailed: "Impossible d'enregistrer votre compte.",
    resetPassword: "Réinitialiser le mot de passe",
    resetPasswordConfirm:
      "Réinitialiser le mot de passe de {name} à la valeur par défaut de la boutique ? Cette personne devra se reconnecter avec et en choisir un nouveau.",
    resetPasswordFailed: "Impossible de réinitialiser ce mot de passe.",
    passwordResetTo:
      "Le mot de passe de {name} a été réinitialisé à « {password} » — dites-le lui pour se reconnecter.",
  },

  ar: {
    permissions: {
      edit: "الصلاحيات",
      title: "ما يمكن لـ {name} فعله",
      loadFailed: "تعذّر تحميل قائمة الصلاحيات.",
      saved: "تم حفظ صلاحيات {name}.",
      everything: "كل شيء",
      count: {
        zero: "بدون صلاحيات",
        one: "صلاحية واحدة",
        two: "صلاحيتان",
        few: "{count} صلاحيات",
        many: "{count} صلاحية",
        other: "{count} صلاحية",
      },
      role: "الدور",
      roleName: { owner: "مالك", staff: "موظف" },
      roleHint: {
        owner: "كل شيء، بما في ذلك الفريق والأجهزة والسجل.",
        staff: "فقط ما تحدده أدناه.",
      },
      startFrom: "البدء من",
      custom: "مخصص",
      presets: {
        cashier: "أمين صندوق",
        stock_keeper: "أمين مخزن",
        manager: "مدير",
        full: "وصول كامل",
      },
      screens: "الشاشات",
      bringsCosts: "مبنية على أسعار الشراء — تمنح أيضاً «رؤية أسعار الشراء»",
      actions: { view: "عرض", stock: "تعديل المخزون", edit: "تعديل", delete: "حذف" },
      areas: {
        products: "المنتجات والتشكيلات",
        packs: "الباقات",
        offers: "العروض",
        purchases: "سجل المبيعات",
        preorders: "الطلبات المسبقة",
        credit: "ديون الزبائن",
        deliveries: "التوريدات",
        suppliers: "الموردون والديون",
        expenses: "المصاريف",
        analytics: "لوحة التحكم والتحليلات",
        revenue: "صافي الإيرادات",
        mixing: "الخلطات",
        shop: "متجرك",
        settings: "الإعدادات",
      },
      powersTitle: "يمكنه أيضاً",
      powers: {
        see_costs: {
          label: "رؤية أسعار الشراء",
          hint: "ما تدفعه للموردين والهوامش وقيمة المخزون بسعر التكلفة — في كل الشاشات.",
        },
        refund: { label: "استرداد المبيعات", hint: "في الصندوق ومن سجل المبيعات." },
        extend_credit: {
          label: "البيع بالدين",
          hint: "السماح لزبون بأخذ البضاعة والدفع لاحقاً.",
        },
        manage_cash: {
          label: "إخراج المال من الدرج",
          hint: "السحوبات والدفع للموردين من الصندوق.",
        },
      },
      ownerOnlyNote: "الفريق والأجهزة والسجل ومدخراتك تبقى لك وحدك دائماً، مهما كان المحدد.",
    },
    title: "الفريق",
    subtitle: "من يعمل هنا، وما يمكن لكل شخص رؤيته وفعله.",
    add: "إضافة شخص",
    loadFailed: "تعذّر تحميل قائمة الفريق.",
    added: "تمت الإضافة إلى المتجر.",
    addedKeptPassword:
      "تمت الإضافة إلى المتجر. هذا البريد الإلكتروني لديه حساب خاص به بالفعل، لذلك يحتفظ بكلمة المرور الحالية ولم يتم تطبيق كلمة المرور التي أدخلتها.",
    addFailed: "تعذّر إضافة هذا الشخص.",
    phoneRequired: "يجب إدخال رقم هاتف للشخص الجديد.",
    phoneTaken: "رقم الهاتف هذا يخص حساباً آخر. اتركه فارغاً أو استعمل رقماً آخر.",
    changeFailed: "تعذّر حفظ هذا التغيير.",
    pinSet: "يمكن لـ {name} الآن فتح نقطة البيع.",
    pinFailed: "تعذّر حفظ هذا الرمز.",
    removed: "لم يعد {name} يعمل هنا.",
    removeFailed: "تعذّر إزالة هذا الشخص.",
    existingHint:
      "إن كان لهذا الشخص حساب مسبقاً، يكفي بريده الإلكتروني — سيتم ربطه بدلاً من إنشاء حساب مكرر.",
    email: "البريد الإلكتروني",
    name: "الاسم",
    phone: "الهاتف",
    password: "كلمة المرور",
    newOnly: "(للأشخاص الجدد فقط)",
    pin: "رمز نقطة البيع",
    fourDigits: "(4 أرقام)",
    adding: "جارٍ الإضافة…",
    addToShop: "إضافة إلى المتجر",
    roles: { owner: "مالك", staff: "موظف" },
    hasPin: "تم تعيين الرمز",
    noPin: "بدون رمز",
    changePin: "تغيير الرمز",
    setPin: "تعيين رمز",
    pinPlaceholder: "4 أرقام",
    savePin: "حفظ الرمز",
    removePin: "حذف الرمز",
    you: "أنت",
    editAccount: "تعديل حسابي",
    myAccount: "حسابي",
    currentPassword: "كلمة المرور الحالية",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "تأكيد كلمة المرور الجديدة",
    passwordMismatch: "كلمة المرور الجديدة وتأكيدها غير متطابقين.",
    savePassword: "تغيير كلمة المرور",
    passwordUpdated: "تم تغيير كلمة مرورك.",
    passwordChangeFailed: "تعذّر تغيير كلمة مرورك.",
    saveAccount: "حفظ",
    accountUpdated: "تم تحديث حسابك.",
    accountUpdateFailed: "تعذّر حفظ حسابك.",
    resetPassword: "إعادة تعيين كلمة المرور",
    resetPasswordConfirm:
      "إعادة تعيين كلمة مرور {name} إلى القيمة الافتراضية للمتجر؟ سيحتاج إلى تسجيل الدخول بها واختيار كلمة مرور جديدة.",
    resetPasswordFailed: "تعذّر إعادة تعيين كلمة المرور.",
    passwordResetTo:
      "أُعيدت تعيين كلمة مرور {name} إلى «{password}» — أخبره بها ليسجّل الدخول.",
  },
};

export default staff;
